import { useEffect, useRef, useState } from "react";

const KEY = "mmedia_intro_shown_v7";
const VIDEO_SRC = "/intro.mp4";

// Phases: video → logo → done
const VIDEO_MAX_MS = 13000;  // hard cap (let the source video finish + 1.5s extra time)
const LOGO_HOLD_MS = 3700;   // logo + tagline hold (gives ~1.5s extra reading time)
const FADE_OUT_MS  = 900;    // gentler stage fade-out

export default function IntroAnimation() {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    // Suppress the intro when a buyer is arriving from checkout — the
    // WelcomeOverlay takes that role and the two collide visually.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("welcome") === "1") return false;
    } catch (_) { /* noop */ }
    return !window.sessionStorage.getItem(KEY);
  });
  const [phase, setPhase] = useState("video"); // "video" | "logo"
  const overlayRef = useRef(null);
  const videoRef = useRef(null);
  const closedRef = useRef(false);

  // Close the overlay (fade then unmount)
  const close = () => {
    if (closedRef.current) return;
    closedRef.current = true;
    try { window.sessionStorage.setItem(KEY, "1"); } catch (_) { /* noop */ }
    document.body.style.overflow = "";
    if (overlayRef.current) {
      overlayRef.current.classList.add("intro-fadeout");
      window.setTimeout(() => setShow(false), FADE_OUT_MS);
    } else {
      setShow(false);
    }
  };

  // Lock scroll while overlay is visible
  useEffect(() => {
    if (!show) return undefined;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  // Phase 1 → Phase 2 (video to logo), hard cap fallback
  useEffect(() => {
    if (!show || phase !== "video") return undefined;
    const fallback = window.setTimeout(() => setPhase("logo"), VIDEO_MAX_MS);
    return () => window.clearTimeout(fallback);
  }, [show, phase]);

  // Phase 2 hold → close
  useEffect(() => {
    if (!show || phase !== "logo") return undefined;
    const t = window.setTimeout(close, LOGO_HOLD_MS);
    return () => window.clearTimeout(t);
  }, [show, phase]);

  // ESC to skip
  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show]);

  if (!show) return null;

  return (
    <>
      <style>{INTRO_CSS}</style>
      <div ref={overlayRef} className="intro-stage" data-testid="intro-overlay">
        <button onClick={close} className="intro-skip" data-testid="intro-skip">
          Skip intro →
        </button>

        {phase === "video" && (
          <div className="intro-video-wrap" data-testid="intro-video-stage">
            <video
              ref={videoRef}
              className="intro-video"
              src={VIDEO_SRC}
              autoPlay
              muted
              playsInline
              preload="auto"
              onEnded={() => setPhase("logo")}
              onError={() => setPhase("logo")}
              data-testid="intro-video"
            />
            <div className="intro-video-vignette" aria-hidden />
            <div className="intro-video-grain" aria-hidden />
          </div>
        )}

        {phase === "logo" && (
          <div className="intro-logo-wrap" data-testid="intro-logo-stage">
            <div className="intro-logo" data-testid="intro-logo">
              <img
                src="/brand/mmedia-logo-light-2x.png"
                alt="M Media Corp"
                className="il-mark"
                draggable="false"
              />
              <span className="il-sub">— the carpet is yours —</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const INTRO_CSS = `
.intro-stage {
  position: fixed; inset: 0; z-index: 200;
  background: #000;
  overflow: hidden; color: #F3F0E9;
  font-family: 'Outfit', sans-serif;
  animation: introStageIn 0.4s ease forwards;
}
.intro-stage.intro-fadeout { animation: introStageOut ${FADE_OUT_MS}ms ease forwards; }
@keyframes introStageIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes introStageOut { to { opacity: 0; } }

.intro-skip {
  position: absolute; top: 22px; right: 22px; z-index: 50;
  background: transparent; border: 1px solid rgba(243,240,233,0.4);
  color: rgba(243,240,233,0.7); padding: 8px 14px;
  font-size: 11px; letter-spacing: 0.25em; text-transform: uppercase;
  cursor: pointer; transition: all 0.3s ease;
}
.intro-skip:hover { color: #F3F0E9; border-color: #F3F0E9; }

/* PHASE 1 — VIDEO */
.intro-video-wrap {
  position: absolute; inset: 0;
  background: #000;
  animation: introVideoIn 0.6s ease forwards;
}
.intro-video {
  position: absolute; inset: 0;
  width: 100%; height: 100%; object-fit: cover;
  filter: contrast(1.05) brightness(1.0);
}
.intro-video-vignette {
  position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(ellipse 80% 90% at 50% 50%, transparent 50%, rgba(0,0,0,0.55) 100%);
}
.intro-video-grain {
  position: absolute; inset: 0; pointer-events: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.4'/></svg>");
  mix-blend-mode: overlay;
  opacity: 0.35;
}
@keyframes introVideoIn { from { opacity: 0; } to { opacity: 1; } }

/* PHASE 2 — LOGO */
.intro-logo-wrap {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: radial-gradient(ellipse at 50% 50%, #0a0a0b 0%, #000 70%);
  animation: introLogoBgIn 0.6s ease forwards;
}
@keyframes introLogoBgIn { from { opacity: 0; } to { opacity: 1; } }

.intro-logo {
  display: flex; flex-direction: column; align-items: center;
  color: #F3F0E9;
  opacity: 0;
  transform: scale(0.96);
  animation: introLogoIn 2.1s cubic-bezier(0.22,1,0.36,1) 0.35s forwards;
}
@keyframes introLogoIn {
  0%   { opacity: 0; transform: scale(0.96); letter-spacing: 0.02em; }
  60%  { opacity: 1; transform: scale(1.0); }
  100% { opacity: 1; transform: scale(1.0); }
}
.il-mark {
  width: clamp(220px, 32vw, 460px);
  height: auto;
  filter: drop-shadow(0 0 60px rgba(243,240,233,0.25));
}
.il-sub {
  font-family: 'Cormorant Garamond', serif; font-style: italic; font-weight: 300;
  font-size: 22px; color: rgba(243,240,233,0.75); margin-top: 28px; letter-spacing: 0.02em;
}

/* Reduced motion: collapse to a simple logo-only flash */
@media (prefers-reduced-motion: reduce) {
  .intro-video { animation: none !important; }
  .intro-logo { animation: none !important; opacity: 1; transform: none; }
}
`;
